import { Column, Grid, Image, Link, MarkdownRenderer, Text } from '@aurora-components';
import { type FragmentType, getFragmentData } from '@gql';
import type { CloudinaryCropResize, CloudinaryGravity, ObjectFit } from 'components/aurora/image/types';
import { useParams } from 'next/navigation';
import { getImage } from 'utils/get-image';
import { BannerFragment } from './banner-gql';
import { HeroContainer, HeroContent, HeroImageContainer, HeroLinkContainer, HeroTextBox } from './banner.styles';
import { MobileOrder } from './types';

type Props = FragmentType<typeof BannerFragment>;

export function Banner(props: Props) {
  const { banner } = getFragmentData(BannerFragment, props);
  const { locale } = useParams() as { locale: string };

  /**
   * * banner.layout has 2 options: 'image_left', 'image_right' in the CMS
   * * We can use this to determine the order of the grid columns
   * the order prop is used to set the order of the grid columns
   * and can also be used to manipulate the order depending on breakpoints.
   * banner.rounded and banner.styled are booleans to determine if the image
   * should be rounded or square and styled by a yellow border
   */
  const isLeft = banner?.layout === 'image_left';
  const isTop = banner?.mobile_layout !== 'image_top';
  const { imgConnection, alt_text, gravity, crop_resize } = banner?.image || {};

  const imageData = getImage(imgConnection);

  const imageSize = imageData?.dimension?.height;

  const { internal_linkConnection, external_link } = banner?.cta || {};
  const internalConnection = internal_linkConnection?.edges![0]?.node;
  const linkTitle = internalConnection?.navigation_title ?? external_link?.title;
  const linkHref = internalConnection?.url
    ? `${locale === 'en' ? '/en' : ''}${internalConnection.url}`
    : external_link?.href;
  const hasLink = !!linkTitle && !!linkHref;
  const expandedImage = banner?.expand_image ?? false;

  const getMobileOrder = (mobileLayout?: string | undefined | null) => {
    const key = mobileLayout as keyof typeof MobileOrder;
    return MobileOrder[key ?? 2];
  };

  if (!imageData) return <></>;

  return (
    <HeroContainer data-testid="hero-banner" $expandedImage={expandedImage}>
      <Grid $fullHeight $fullWidth>
        <Column
          $span={{ xs: 12, md: 6, xl: expandedImage ? 6 : 8 }}
          $order={{
            xs: isTop ? 1 : 2,
            md: isLeft ? 2 : 1,
          }}
          $verticalAlign="center"
        >
          <HeroContent>
            <HeroTextBox>
              {banner?.headline && (
                <MarkdownRenderer
                  $variant={banner?.main_banner ? 'h3' : 'h4'}
                  $color="primary-base100"
                  $paddingBottom="h3"
                  $paddingTop="none"
                  $noBalancer
                  $fontWeight="Normal"
                  markdown={banner?.headline || ''}
                />
              )}
            </HeroTextBox>
            <HeroTextBox $visible={{ xs: banner?.mobile_layout === 'image_top', md: true }}>
              {banner?.paragraph && (
                <MarkdownRenderer
                  $variant="span"
                  $color="primary-base100"
                  $fontWeight="Light"
                  $noBalancer
                  markdown={banner.paragraph}
                />
              )}
              {hasLink && (
                <HeroLinkContainer>
                  <Link title={linkTitle} url={linkHref} />
                </HeroLinkContainer>
              )}
            </HeroTextBox>
          </HeroContent>
        </Column>
        <Column
          $span={{ xs: 12, md: 6, xl: expandedImage ? 6 : 3 }}
          $order={{
            xs: getMobileOrder(banner?.mobile_layout),
            md: isLeft ? 1 : 2,
          }}
          $horizontalAlign="center"
          $verticalAlign="center"
        >
          {imageData && (
            <HeroImageContainer
              $rounded={banner!.rounded}
              $styled={banner!.styled}
              $height={imageSize!}
              $expandedImage={expandedImage}
            >
              <Image
                imageId={imageData?.id || ''}
                priority={true}
                alt={alt_text || ''}
                imageSuffix={imageData?.suffix || ''}
                gravity={gravity as CloudinaryGravity}
                crop_resize={crop_resize as CloudinaryCropResize}
                objectFit={(banner?.object_fit ?? 'contain') as ObjectFit}
                rounded={banner?.rounded ?? false}
              />
            </HeroImageContainer>
          )}
        </Column>
        <Column $span={{ xs: isTop ? 12 : 0, md: 0 }} $order={{ xs: banner?.mobile_layout !== 'image_bottom' ? 3 : 2 }}>
          <HeroContent>
            <HeroTextBox $visible={{ xs: isTop, md: false }}>
              {banner?.paragraph && (
                <Text $variant="p" $color="primary-base100" $fontWeight="Light" $noBalancer>
                  {banner.paragraph}
                </Text>
              )}
              {hasLink && (
                <HeroLinkContainer>
                  <Link title={linkTitle} url={linkHref} />
                </HeroLinkContainer>
              )}
            </HeroTextBox>
          </HeroContent>
        </Column>
      </Grid>
    </HeroContainer>
  );
}
